import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

import 'bootstrap/dist/css/bootstrap.min.css';
import '../assets/css/psearch.css';
import '../assets/css/animations.css';

import apiClient from '../config/config';

import { debounce } from 'lodash';
import { supabase } from '../supabaseClient';
import SignInModal from './SignInModal';
import CurrencySelector from './CurrencySelector';
import { useCurrency } from '../context/CurrencyContext';


function Nav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { currency, getCurrencySymbol } = useCurrency();
  const [query, setQuery] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [timeoutReached, setTimeoutReached] = useState(false);
  const [brands, setBrands] = useState([]);
  const [productCount, setProductCount] = useState(0);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [tryOnOpen, setTryOnOpen] = useState(false);
  const [tryOnImage, setTryOnImage] = useState(null);
  const [user, setUser] = useState(null);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [signInModalMessage, setSignInModalMessage] = useState('');
  const [pendingTryOnImage, setPendingTryOnImage] = useState(null);
  const [authEmail, setAuthEmail] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [authMessage, setAuthMessage] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [savedProducts, setSavedProducts] = useState([]);
  const [avatarMode, setAvatarMode] = useState('image');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [liveResults, setLiveResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [liveLoading, setLiveLoading] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);


  useEffect(() => {
    const getSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user ?? null);
    };

    getSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setUser(session?.user ?? null);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const avatarUrl = user?.user_metadata?.avatar_url ? String(user.user_metadata.avatar_url) : '';
  const userInitials = useMemo(() => {
    const rawName = user?.user_metadata?.full_name ? String(user.user_metadata.full_name) : '';
    const email = user?.email ? String(user.email) : '';

    const source = rawName.trim() || email.trim();
    if (!source) return '';

    const parts = source
      .replace(/@.*/, '')
      .split(/\s+/)
      .filter(Boolean);

    if (parts.length === 0) return '';

    const first = parts[0]?.[0] ?? '';
    const second = parts.length > 1 ? (parts[1]?.[0] ?? '') : (parts[0]?.[1] ?? '');
    return `${first}${second}`.toUpperCase();
  }, [user]);

  useEffect(() => {
    if (!user) return;
    if (avatarUrl) {
      setAvatarMode('image');
      return;
    }
    if (userInitials) {
      setAvatarMode('initials');
      return;
    }
    setAvatarMode('fallback');
  }, [user, avatarUrl, userInitials]);

    
  const handleTryOn = (image) => {
    if (!user) {
      // Show sign in modal if not logged in
      setPendingTryOnImage(image);
      setShowSignInModal(true);
      return;
    }
    setTryOnImage(image);
    setTryOnOpen(true);
  };

  const handleGoogleSignIn = async () => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      
      if (error) {
        console.error('Error signing in:', error.message);
        alert(`Sign in error: ${error.message}`);
      } else {
        console.log('OAuth initiated:', data);
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      alert(`Unexpected error: ${err.message}`);
    }
  };

  const handleSignOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Error signing out:', error.message);
    } else {
      navigate('/');
    }
  };

  const handleEmailSignIn = async (e) => {
    e.preventDefault();
    if (!authEmail) return;
    
    setAuthLoading(true);
    setAuthMessage('');
    
    const { error } = await supabase.auth.signInWithOtp({
      email: authEmail,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    
    if (error) {
      setAuthMessage(`Error: ${error.message}`);
    } else {
      setAuthMessage('Check your email for the magic link!');
    }
    setAuthLoading(false);
  };

  const handleEmailPasswordSignIn = async (email, password) => {
    setAuthLoading(true);
    setAuthMessage('');
    
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (error) {
      setAuthMessage(`Error: ${error.message}`);
    } else {
      setShowSignInModal(false);
    }
    setAuthLoading(false);
  };

  const handleEmailPasswordSignUp = async (email, password) => {
    setAuthLoading(true);
    setAuthMessage('');
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
        data: { user_role: 'general' },
      },
    });
    
    if (error) {
      setAuthMessage(`Error: ${error.message}`);
    } else {
      setAuthMessage('Check your email to confirm your account!');
    }
    setAuthLoading(false);
  };

  // Open try-on after successful sign in if there was a pending image
  useEffect(() => {
    if (user && pendingTryOnImage) {
      setTryOnImage(pendingTryOnImage);
      setTryOnOpen(true);
      setPendingTryOnImage(null);
      setShowSignInModal(false);
    }
  }, [user, pendingTryOnImage]);

  const debouncedSearch = useCallback(
    debounce(async (searchQuery) => {
      if (searchQuery.length > 1) {
        setLiveLoading(true);
        try {
          const response = await apiClient.get('/products/search/', {
            params: { q: searchQuery, currency: currency }
          });
          setLiveResults(response.data.slice(0, 6));
          setShowDropdown(true);
        } catch (err) {
          console.error('Live search error:', err);
          setLiveResults([]);
        } finally {
          setLiveLoading(false);
        }
      } else {
        setLiveResults([]);
        setShowDropdown(false);
      }
    }, 300),
    [currency]
  );

  useEffect(() => {
    let timer;
    if (loading) {
      timer = setTimeout(() => {
        setTimeoutReached(true);
        setLoading(false);
      }, 8000);
    }
    return () => clearTimeout(timer);
  }, [loading]);

  const handleCategorySelect = async (selection) => {
  
    setSelectedCategory(selection);
    setLoading(true);
    setError(null);
    setSearchPerformed(true);
    setTimeoutReached(false);
  
    try {
      const params = {
        category: selection === 'All' ? '' : selection,
        q: query || undefined,
        currency: currency,
      };
  
      const response = await apiClient.get('/products/search/', {
        params: params,
      });
  
      setProducts(response.data);
      setProductCount(response.data.length); 
    } catch (err) {
      setError('Failed to fetch products. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProductsByBrand = async (brand) => {
    if (loading || selectedBrand === brand) return; 
    setLoading(true);
    setError(null);
    setSearchPerformed(true);
    setTimeoutReached(false);
  
    try {
      const response = await apiClient.get('/products/search/', {
        params: { brand },
        currency: currency,
      });
  
      setProducts(response.data);
      setProductCount(response.data.length); 
      setSelectedBrand(brand); 
    } catch (err) {
      setError('Failed to fetch products. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = useMemo(() => {
    return products.filter(product => 
      product.title?.toLowerCase().includes(query.toLowerCase()) ||
      product.brand?.toLowerCase().includes(query.toLowerCase())
    );
  }, [products, query]);

  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    setShowDropdown(false);
    setLiveResults([]);
    setLoading(true);
    setError(null);
    setSearchPerformed(true);
    setTimeoutReached(false);
  
    try {
      const params = {
        q: query,
        category: selectedCategory,
        brand: selectedBrand,
        currency: currency,
      };
  
      const response = await apiClient.get('/products/search/', { params });
  
      setProducts(response.data);
      setProductCount(response.data.length);
    } catch (err) {
      console.error('Error searching products:', err);
      setError('Failed to search products');
    } finally {
      setLoading(false);
    }

    // Navigate to shop page with search results
    navigate(`/shop?q=${encodeURIComponent(query)}`);
    // Reset search after navigation
    setQuery('');
  };

  const resetSearch = () => {
    setSearchPerformed(false);
    setProducts([]);
    setQuery('');
    setSelectedCategory('All');
    setProductCount(0);
  };

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await apiClient.get('/brands/');
        if (response.data) {
          setBrands(response.data);
        }
      } catch (err) {
        console.error('Error fetching brands:', err);
      }
    };

    fetchBrands();
  }, []);

  useEffect(() => {
    if (searchPerformed) {
      handleSearch();
    }
  }, [currency]); 
  

  useEffect(() => {
    if (user) {
      loadSavedProducts(user.id);
    } else {
      setSavedProducts([]);
    }
  }, [user]);

  const loadSavedProducts = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('saved_products')
        .select('product_id')
        .eq('user_id', userId);
      if (data && !error) {
        setSavedProducts(data.map(item => item.product_id));
      }
    } catch (error) {
      console.error('Error loading saved products:', error);
    }
  };

  const toggleSaveProduct = async (productId) => {
    if (!user) {
      setShowSignInModal(true);
      return;
    }
    const isSaved = savedProducts.includes(productId);
    try {
      if (isSaved) {
        await supabase
          .from('saved_products')
          .delete()
          .eq('user_id', user.id)
          .eq('product_id', productId);
        setSavedProducts(savedProducts.filter(id => id !== productId));
      } else {
        await supabase
          .from('saved_products')
          .insert({ user_id: user.id, product_id: productId });
        setSavedProducts([...savedProducts, productId]);
      }
    } catch (error) {
      console.error('Error toggling saved product:', error);
    }
  };

  const scrollToTop = () => {
    window.scrollTo(0, 0);
  };

  return (
    <div>
        <motion.header
          className={`yb-nav-header glass-nav${scrolled ? ' scrolled' : ''}`}
          initial={{ y: -80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
        {/* Logo */}
        <div className="yb-nav-left">
          <div className="yb-nav-logo">
            <a href="/">
              <picture>
                <source srcSet={require('../assets/images/Y.png')} type="image/png" />
                <img
                  src={require('../assets/images/Y.png')}
                  alt="Yemberzal"
                  className="logo-size"
                  loading="eager"
                  decoding="async"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = '/default-image.avif';
                  }}
                />
              </picture>
            </a>
          </div>
        </div>

        {/* Search Bar */}
        <div className="yb-nav-search" style={{ position: 'relative' }}>
          <form onSubmit={handleSearch} style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
            <input
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                debouncedSearch(e.target.value);
              }}
              onFocus={() => {
                if (liveResults.length > 0) setShowDropdown(true);
              }}
              placeholder="Search kashmiri fashion pieces..."
              style={{
                padding: '8px 16px 8px 40px',
                borderTop: 'none',
                borderLeft: 'none',
                borderRight: 'none',
                borderBottom: '1px solid #e0e0e0',
                outline: 'none',
                fontSize: '14px',
                width: '100%',
                maxWidth: '500px',
                minWidth: '0',
                fontFamily: "'Montserrat', sans-serif",
                backgroundColor: '#ffffff',
                transition: 'all 0.2s',
              }}
            />
            <button
              type="submit"
              style={{
                position: 'absolute',
                left: '12px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#666"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
            </button>
          </form>

          {/* Live Search Dropdown */}
          {showDropdown && (
            <>
              <div style={{ position: 'fixed', inset: 0, zIndex: 998 }} onClick={() => setShowDropdown(false)} />
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                width: '100%',
                maxWidth: '400px',
                minWidth: '240px',
                maxHeight: '420px',
                overflowY: 'auto',
                background: '#fff',
                borderRadius: '12px',
                boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                border: '1px solid #eee',
                zIndex: 999,
                marginTop: '8px',
              }}>
                {liveLoading ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#888', fontSize: '13px', fontFamily: 'Montserrat, sans-serif' }}>
                    Searching...
                  </div>
                ) : liveResults.length > 0 ? (
                  <>
                    {liveResults.map((product, idx) => (
                      <div
                        key={product.id || idx}
                        onClick={() => {
                          setShowDropdown(false);
                          setQuery('');
                          navigate(`/product/${product.id || encodeURIComponent(product.link)}`);
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '10px 16px',
                          cursor: 'pointer',
                          borderBottom: idx < liveResults.length - 1 ? '1px solid #f5f5f5' : 'none',
                          transition: 'background 0.15s',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f8f8f8'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                      >
                        <img
                          src={product.image}
                          alt={product.title}
                          style={{
                            width: '48px',
                            height: '60px',
                            objectFit: 'cover',
                            borderRadius: '6px',
                            backgroundColor: '#f5f5f5',
                            flexShrink: 0,
                          }}
                          onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/default-image.avif'; }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{
                            fontSize: '11px',
                            fontWeight: '600',
                            fontFamily: 'Montserrat, sans-serif',
                            color: '#1a1a1a',
                            margin: '0 0 2px 0',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                          }}>
                            {product.brand || 'Unknown Brand'}
                          </p>
                          <p style={{
                            fontSize: '12px',
                            fontFamily: 'Montserrat, sans-serif',
                            color: '#666',
                            margin: '0 0 2px 0',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}>
                            {product.title || 'Unnamed Product'}
                          </p>
                          <p style={{
                            fontSize: '12px',
                            fontFamily: 'Montserrat, sans-serif',
                            color: '#888',
                            margin: 0,
                          }}>
                            {product.price ? `${getCurrencySymbol()} ${product.price}` : ''}
                          </p>
                        </div>
                      </div>
                    ))}
                    <div
                      onClick={() => {
                        setShowDropdown(false);
                        navigate(`/shop?q=${encodeURIComponent(query)}`);
                        setQuery('');
                      }}
                      style={{
                        padding: '12px 16px',
                        textAlign: 'center',
                        fontSize: '13px',
                        fontWeight: '500',
                        fontFamily: 'Montserrat, sans-serif',
                        color: '#1a1a1a',
                        cursor: 'pointer',
                        borderTop: '1px solid #eee',
                        transition: 'background 0.15s',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f8f8f8'}
                      onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                    >
                      View all results for "{query}"
                    </div>
                  </>
                ) : query.length > 1 ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#888', fontSize: '13px', fontFamily: 'Montserrat, sans-serif' }}>
                    No products found for "{query}"
                  </div>
                ) : null}
              </div>
            </>
          )}
        </div>

        {/* Desktop Nav Links */}
        <nav className="yb-nav-links-desktop">
          {[
            { to: '/discover', label: 'Discover' },
            { to: '/shop', label: 'Explore' },
            { to: '/brands', label: 'Brands' },
            { to: '/blog', label: 'Blog' },
            { to: '/partner', label: 'Partner' },
          ].map((link, i) => (
            <motion.div
              key={link.to}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + i * 0.06, duration: 0.4 }}
            >
              <Link
                to={link.to}
                onClick={scrollToTop}
                className={`yb-nav-link${location.pathname === link.to ? ' active' : ''}`}
              >
                {link.label}
              </Link>
            </motion.div>
          ))}
        </nav>

        {/* Right side: user/sign-in + hamburger */}
        <div className="yb-nav-right">
        {user ? (
          <div style={{ position: 'relative', marginBottom: '20px' }}>
            <button
              onClick={() => setShowUserDropdown(!showUserDropdown)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: 'transparent',
                border: '2px solid #f0ebe5',
                borderRadius: '25px',
                padding: '6px 14px 6px 6px',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = '#f0ebe5'}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = '#f0ebe5'}
            >
              {avatarMode === 'image' && (
                <img
                  src={avatarUrl}
                  alt="Profile"
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                  }}
                  onError={() => {
                    if (userInitials) {
                      setAvatarMode('initials');
                    } else {
                      setAvatarMode('fallback');
                    }
                  }}
                />
              )}

              {avatarMode === 'initials' && (
                <div
                  aria-label="Profile"
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: '#f0ebe5',
                    color: '#1a1a1a',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    fontWeight: '600',
                    letterSpacing: '0.6px',
                    userSelect: 'none',
                  }}
                >
                  {userInitials}
                </div>
              )}

              {avatarMode === 'fallback' && (
                <img
                  src={'/default-avatar.jpg'}
                  alt="Profile"
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                  }}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = '/default-avatar.jpg';
                  }}
                />
              )}
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#1a1a1a" strokeWidth="2">
                <path d="M6 9l6 6 6-6"/>
              </svg>
            </button>

            {/* Dropdown Menu */}
            {showUserDropdown && (
              <>
                <div
                  style={{ position: 'fixed', inset: 0, zIndex: 999 }}
                  onClick={() => setShowUserDropdown(false)}
                />
                <div
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    right: 0,
                    background: '#fff',
                    borderRadius: '16px',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                    border: '1px solid #f0ebe5',
                    minWidth: '200px',
                    zIndex: 1000,
                    overflow: 'hidden',
                  }}
                >
                  {/* User Info Header */}
                  {/* <div style={{
                    padding: '16px',
                    borderBottom: '1px solid #f0ebe5',
                    background: 'linear-gradient(180deg, #ffffff 0%, #ffffff 100%)',
                  }}>
                    <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#2d2a26' }}>
                      {user.user_metadata?.full_name || 'User'}
                    </p>
                    <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#6b6560' }}>
                      {user.email}
                    </p>
                  </div> */}

                  {/* Menu Items */}
                  <div style={{ padding: '8px' }}>
                    <Link
                      to="/profile"
                      onClick={() => setShowUserDropdown(false)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '10px 12px',
                        borderRadius: '10px',
                        textDecoration: 'none',
                        color: '#1a1a1a',
                        fontSize: '14px',
                        transition: 'background 0.2s',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f2f2f2'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      
                      Profile
                    </Link>

                    <Link
                      to="/fashion-board"
                      onClick={() => setShowUserDropdown(false)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '10px 12px',
                        borderRadius: '10px',
                        textDecoration: 'none',
                        color: '#1a1a1a',
                        fontSize: '14px',
                        transition: 'background 0.2s',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f2f2f2'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      
                      My Fashion Board
                    </Link>
                  </div>

                  {/* Sign Out */}
                  <div style={{ padding: '8px', borderTop: '1px solid #f0ebe5' }}>
                    <button
                      onClick={() => {
                        handleSignOut();
                        setShowUserDropdown(false);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: '10px',
                        border: 'none',
                        background: 'transparent',
                        color: '#c0392b',
                        fontSize: '14px',
                        cursor: 'pointer',
                        transition: 'background 0.2s',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(192, 57, 43, 0.08)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      
                      Sign Out
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        ) : (
          <button
            onClick={() => {
              setSignInModalMessage('Sign in to access your fashion board and try-on features.');
              setShowSignInModal(true);
            }}
            className="yb-nav-signin ms-3 border border-gray-400 d-none d-md-flex"
            style={{
              borderRadius: '25px',
              padding: '5px 15px',
              marginBottom: '20px',
              background: 'transparent',
              cursor: 'pointer',
              fontSize: '16px',
              color: '#1a1a1a',
              transition: 'background 0.2s, color 0.2s, border-color 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#1a1a1a';
              e.currentTarget.style.color = '#fff';
              e.currentTarget.style.borderColor = '#1a1a1a';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = '#1a1a1a';
              e.currentTarget.style.borderColor = '#cbd5e1';
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
            </svg>
          </button>
        )}

          {/* Hamburger button - mobile only */}
          <button
            className="yb-nav-hamburger"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 12h18M3 6h18M3 18h18" />
              </svg>
            )}
          </button>
        </div>
      </motion.header>

      {/* Currency Selector */}
      <CurrencySelector />

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
      {mobileMenuOpen && (
        <motion.div
          className="yb-mobile-menu-overlay"
          onClick={() => setMobileMenuOpen(false)}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          <motion.nav
            className="yb-mobile-menu"
            onClick={(e) => e.stopPropagation()}
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            style={{
              background: 'rgba(255, 255, 255, 0.92)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
            }}
          >
            {/* User profile section at top when signed in */}
            {user && (
              <div className="yb-mobile-user-section">
                <img
                  src={avatarUrl || '/default-avatar.jpg'}
                  alt="Profile"
                  className="yb-mobile-avatar"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = '/default-avatar.jpg';
                  }}
                />
                <div className="yb-mobile-user-info">
                  <span className="yb-mobile-user-name">{user.user_metadata?.full_name || 'User'}</span>
                  <span className="yb-mobile-user-email">{user.email}</span>
                </div>
              </div>
            )}

            <Link to="/discover" className="yb-mobile-link" onClick={() => { setMobileMenuOpen(false); scrollToTop(); }}>Discover</Link>
                        <Link to="/shop" className="yb-mobile-link" onClick={() => { setMobileMenuOpen(false); scrollToTop(); }}>Explore</Link>
            <Link to="/brands" className="yb-mobile-link" onClick={() => { setMobileMenuOpen(false); scrollToTop(); }}>Brands</Link>
            <Link to="/blog" className="yb-mobile-link" onClick={() => { setMobileMenuOpen(false); scrollToTop(); }}>Blog</Link>
            <Link to="/partner" className="yb-mobile-link" onClick={() => { setMobileMenuOpen(false); scrollToTop(); }}>Partner</Link>

            {user ? (
              <>
                <div className="yb-mobile-divider" />
                <Link to="/profile" className="yb-mobile-link" onClick={() => { setMobileMenuOpen(false); scrollToTop(); }}>Profile</Link>
                <Link to="/fashion-board" className="yb-mobile-link" onClick={() => { setMobileMenuOpen(false); scrollToTop(); }}>My Fashion Board</Link>
                <button
                  className="yb-mobile-signout-btn"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    handleSignOut();
                  }}
                >
                  Sign Out
                </button>
              </>
            ) : (
              <button
                className="yb-mobile-signin-btn"
                onClick={() => {
                  setMobileMenuOpen(false);
                  setSignInModalMessage('Sign in to access your fashion board and try-on features.');
                  setShowSignInModal(true);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                </svg>
                Sign In
              </button>
            )}
          </motion.nav>
        </motion.div>
      )}
      </AnimatePresence>

      <SignInModal
        open={showSignInModal}
        onClose={() => {
          setShowSignInModal(false);
          setAuthMessage('');
          setAuthEmail('');
          setSignInModalMessage('');
        }}
        message={signInModalMessage}
        authEmail={authEmail}
        setAuthEmail={setAuthEmail}
        authLoading={authLoading}
        authMessage={authMessage}
        onEmailSignIn={handleEmailSignIn}
        onGoogleSignIn={handleGoogleSignIn}
        onEmailPasswordSignIn={handleEmailPasswordSignIn}
        onEmailPasswordSignUp={handleEmailPasswordSignUp}
      />
    </div>
  )
}

export default Nav;
